import React from 'react';
import { EventQuestionResponseQuery, EventsQuery, EventRsvpDataQuery } from '../../generated/graphql';
import TextAnswerResponseCard from './TextAnswerResponseCard';
import OptionDataResponseCard from './OptionDataResponseCard';
import RsvpDataResponseCard from './RsvpDataResponseCard';
import styles from './rsvpSummaryCard.scss';
import { Body1 } from '@xo-union/tk-ui-typography';
import { Divider } from '../SettingsRail/Divider';
import Icon from '@xo-union/tk-component-icons';
import { Action, StatusText } from '../Card';
import downloadEventQuestionResponses from '../../helpers/downloadEventQuestionResponses';
import { findEventById, findEventRsvpDataById } from './utils';
import { useHistory } from 'react-router-dom';

interface Props {
  eventQuestionsData: EventQuestionResponseQuery | undefined;
  eventData: EventsQuery | undefined;
  rsvpData: EventRsvpDataQuery | undefined;
  isPrivateRsvp: boolean;
  dataColors: string[];
}

const EventQuestionCards: React.FC<Props> = ({
  eventQuestionsData,
  eventData,
  rsvpData,
  isPrivateRsvp,
  dataColors,
}) => {
  const history = useHistory();

  return (
    <>
      {eventQuestionsData?.eventQuestionResponseData?.map((event, index) => {
        const eventRsvpData = findEventRsvpDataById(rsvpData, event.eventId);
        const eventDataObj = findEventById(eventData, event.eventId);
        const { name = '', requireRsvp = false } = eventDataObj || {};
        const guestCount = eventRsvpData?.guestCount || 0;

        return (
          <React.Fragment key={index}>
            <div className={styles.eventName}>
              <Body1 bold className={styles.questionSubhead} as="h3">
                {name}
              </Body1>
              {isPrivateRsvp && !!guestCount && !!requireRsvp && (
                <div className={styles.invitedCount}>
                  <Icon name="guest_couple" />
                  {guestCount} Guests Invited
                </div>
              )}
              {!requireRsvp && <StatusText className={styles.notCollectingRsvp}>Not Collecting RSVPs</StatusText>}
            </div>

            {!!requireRsvp && (
              <>
                {isPrivateRsvp && !guestCount ? (
                  <div className={styles.addGuestContainer}>
                    <span className={styles.addGuestText}>
                      Heads up! There are <span className={styles.noGuests}>0 guests</span> on your {name} list.
                    </span>
                    <Action
                      onClick={() => history.push(`/our-guest-list/?event=${event.eventId}`)}
                      className={styles.addGuestLink}
                      data-testid={`add-guest-${event.eventId}`}
                    >
                      + Add Guest
                    </Action>
                  </div>
                ) : (
                  <div className={styles.questionContainer}>
                    <RsvpDataResponseCard data={eventRsvpData} isPrivateRsvp={isPrivateRsvp} />
                    <Action
                      data-testid={`rsvp-goto-glm-action-${event.eventId}`}
                      onClick={() => history.push(`/our-guest-list/?event=${event.eventId}`)}
                    >
                      Manage Guest List
                    </Action>
                  </div>
                )}

                {event.questionsData?.map((question, index) => {
                  if (question?.answerType === 'Text') {
                    return (
                      <div className={styles.questionContainer} key={index}>
                        <TextAnswerResponseCard question={question} />
                        <Action
                          data-testid={`rsvp-text-download-action-${question.questionId}`}
                          onClick={() => downloadEventQuestionResponses(question.questionId)}
                        >
                          {' '}
                          Download{' '}
                        </Action>
                      </div>
                    );
                  } else if (question?.answerType === 'Option') {
                    return (
                      <div className={styles.questionContainer} key={index}>
                        <OptionDataResponseCard key={question.questionId} data={question} colors={dataColors} />
                        <Action
                          data-testid={`rsvp-option-download-action-${question.questionId}`}
                          onClick={() => downloadEventQuestionResponses(question.questionId)}
                        >
                          {' '}
                          Download{' '}
                        </Action>
                      </div>
                    );
                  }
                })}
              </>
            )}

            {index + 1 !== eventQuestionsData?.eventQuestionResponseData?.length && (
              <Divider additionalStyles={styles.fullWidth} />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default EventQuestionCards;
